import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  updateSEOTags, 
  addStructuredData, 
  setCanonicalUrl,
  generateStructuredData,
  PAGE_SEO_CONFIG 
} from '../services/seoService';

// Custom hook for managing SEO on each page
export const useSEO = (pageConfig = {}) => {
  const location = useLocation();

  useEffect(() => {
    // Get page-specific config based on route
    const routeConfig = getRouteConfig(location.pathname);
    const finalConfig = { 
      ...routeConfig, 
      ...pageConfig,
      path: location.pathname 
    };

    // Update meta tags
    updateSEOTags(finalConfig);
    
    // Set canonical URL
    setCanonicalUrl(location.pathname);
    
    // Add basic structured data
    if (finalConfig.structuredData) {
      addStructuredData(finalConfig.structuredData);
    } else {
      // Add default organization structured data
      const orgData = generateStructuredData('Organization');
      addStructuredData(orgData);
    }
  }, [location.pathname, pageConfig]);
};

// Get SEO config based on route
const getRouteConfig = (pathname) => {
  // Home page
  if (pathname === '/' || pathname === '/home') {
    return PAGE_SEO_CONFIG.home;
  }
  
  // About pages
  if (pathname.startsWith('/about')) {
    return PAGE_SEO_CONFIG.about;
  }
  
  // Membership pages
  if (pathname.includes('membership')) {
    return PAGE_SEO_CONFIG.membership;
  }
  
  // Events pages
  if (pathname.startsWith('/events')) {
    return PAGE_SEO_CONFIG.events;
  }
  
  // Jobs pages
  if (pathname.startsWith('/jobs')) {
    return PAGE_SEO_CONFIG.jobs;
  }
  
  // Projects pages
  if (pathname.startsWith('/projects')) {
    return PAGE_SEO_CONFIG.projects;
  }
  
  // Directory pages
  if (pathname.startsWith('/directory') || pathname.startsWith('/members')) {
    return PAGE_SEO_CONFIG.directory;
  }
  
  // Groups pages
  if (pathname.startsWith('/groups')) {
    return PAGE_SEO_CONFIG.groups;
  }
  
  // Default fallback
  return PAGE_SEO_CONFIG.home;
};

// Hook for job detail pages
export const useJobSEO = (job) => {
  const seoConfig = job ? {
    title: `${job.title} at ${job.company} | Finance Jobs | APN`,
    description: `${job.title} position at ${job.company} in ${job.location}. ${job.description?.substring(0, 150)}... Apply through APN's exclusive finance jobs network.`,
    keywords: [
      job.title?.toLowerCase(),
      job.company?.toLowerCase(),
      job.location?.toLowerCase(),
      'finance jobs',
      'investment banking',
      job.jobType?.toLowerCase()
    ].filter(Boolean),
    image: job.companyLogo,
    structuredData: generateStructuredData('JobPosting', {
      title: job.title,
      description: job.description,
      company: job.company,
      location: job.location,
      jobType: job.jobType,
      datePosted: job.postedDate,
      validThrough: job.applicationDeadline,
      salary: job.salaryRange
    })
  } : {};

  useSEO(seoConfig);
};

// Hook for event detail pages
export const useEventSEO = (event) => {
  const seoConfig = event ? {
    title: `${event.title} | Finance Networking Event | APN`,
    description: `Join ${event.title} - ${event.description?.substring(0, 150)}... Network with finance professionals at this exclusive APN event.`,
    keywords: [
      event.title?.toLowerCase(),
      'finance event',
      'networking event',
      event.category?.toLowerCase(),
      event.location?.toLowerCase()
    ].filter(Boolean),
    image: event.image,
    structuredData: generateStructuredData('Event', {
      title: event.title,
      description: event.description,
      startDate: event.startDate,
      endDate: event.endDate,
      location: event.location,
      isVirtual: event.isVirtual,
      ticketPrice: event.ticketTypes?.[0]?.price
    })
  } : {};

  useSEO(seoConfig);
};

// Hook for news/article pages
export const useArticleSEO = (article) => {
  const seoConfig = article ? {
    title: `${article.title} | APN Finance News & Insights`,
    description: article.excerpt || `${article.content?.substring(0, 150)}...`,
    keywords: [
      article.title?.toLowerCase(),
      'finance news',
      'industry insights',
      article.category?.toLowerCase()
    ].filter(Boolean),
    image: article.image,
    structuredData: generateStructuredData('Article', {
      title: article.title,
      excerpt: article.excerpt,
      author: article.author,
      publishedAt: article.publishedAt,
      updatedAt: article.updatedAt,
      image: article.image,
      slug: article.slug
    })
  } : {};

  useSEO(seoConfig);
};

// Hook for project detail pages
export const useProjectSEO = (project) => {
  const seoConfig = project ? {
    title: `${project.title} | Finance Consulting Project | APN`,
    description: `${project.title} - ${project.description?.substring(0, 150)}... Apply for this finance consulting opportunity through APN.`,
    keywords: [
      project.title?.toLowerCase(),
      'finance consulting',
      'financial modeling',
      project.category?.toLowerCase(),
      project.skills?.map(s => s.toLowerCase())
    ].flat().filter(Boolean),
    image: project.image
  } : {};

  useSEO(seoConfig);
};

// Hook for user profile pages
export const useProfileSEO = (user) => {
  const seoConfig = user ? {
    title: `${user.displayName || user.firstName + ' ' + user.lastName} | Finance Professional | APN`,
    description: `Connect with ${user.displayName || user.firstName + ' ' + user.lastName}, ${user.jobTitle} at ${user.company}. ${user.aboutMe?.substring(0, 150)}...`,
    keywords: [
      user.displayName?.toLowerCase(),
      user.jobTitle?.toLowerCase(),
      user.company?.toLowerCase(),
      'finance professional',
      'networking'
    ].filter(Boolean),
    image: user.profileImage
  } : {};

  useSEO(seoConfig);
};

export default useSEO;
